import { useState, useEffect, useRef, useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { Menu, BarChart2, Copy } from "lucide-react";
import { C } from "../constants/colors";
import { timeAgo } from "../utils/admin";
import { UsageChart } from "./UsageChart";
import { UsageByUser } from "./UsageByUser";
import { UsageByModule } from "./UsageByModule";
import { AdminUsersTable } from "./AdminUsersTable";

export function AdminPanel({ isMobile, onMenuOpen, onBack }) {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [usageStats, setUsageStats] = useState(null);
  const [usageView, setUsageView] = useState("date");
  const [focusUserId, setFocusUserId] = useState(null);
  /* Her ask, 2026-10-08: a person on the Users tab opens their By User row. */
  const openUserUsage = (id) => { setFocusUserId(id); setUsageView("user"); setTab("usage_stats"); };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [grantingId, setGrantingId] = useState(null);
  const [creditsOn, setCreditsOn] = useState(true);
  const [unmatched, setUnmatched] = useState([]);
  const loaded = useRef({});
  const { session } = useSession();

  const grantAccess = async (targetUserId, days) => {
    setGrantingId(`${targetUserId}-${days}`);
    try {
      const token = await session?.getToken();
      const res = await fetch("/api/admin", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "grant_access", targetUserId, days }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      setUsers(prev => prev.map(u =>
        u.id === targetUserId ? { ...u, phase: "ideation", ideationExpiresAt: data.expiresAt } : u
      ));
    } catch (err) {
      alert(`Failed to grant access: ${err.message}`);
    }
    setGrantingId(null);
  };

  /* A payment that bought nothing, put right by hand — off the list. */
  const resolveUnmatched = async (id) => {
    try {
      const token = await session?.getToken();
      const res = await fetch("/api/admin", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "resolve_unmatched", id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      setUnmatched(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      alert(`Couldn't mark it sorted: ${err.message}`);
    }
  };

  /* Comps, refunds, a payment that came through unmatched (api/admin.js
     grant_credits). A negative number takes credits back. */
  const giveCredits = async (u) => {
    const raw = window.prompt(`Give ${u.name || u.email} how many credits? (100 credits = $1 of Brill. A minus number takes them back.)`, "200");
    if (raw === null) return;
    const credits = Number(raw);
    if (!Number.isFinite(credits) || credits === 0) { alert("That isn't a number of credits."); return; }
    const note = window.prompt("A note for the record (why):", "Given by Tevra");
    if (note === null) return;
    setGrantingId(`${u.id}-credits`);
    try {
      const token = await session?.getToken();
      const res = await fetch("/api/admin", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "grant_credits", targetUserId: u.id, credits, note }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, credits: data.credits } : x));
    } catch (err) {
      alert(`Failed to give credits: ${err.message}`);
    }
    setGrantingId(null);
  };

  const load = useCallback(async (t) => {
    if (loaded.current[t]) return;
    setLoading(true);
    setError(null);
    try {
      const token = await session?.getToken();
      const res = await fetch("/api/admin", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: t }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      if (t === "users") {
        setUsers(data.users || []);
        setCreditsOn(data.creditsOn !== false);
        setUnmatched(data.unmatched || []);
      }
      if (t === "testimonials") setTestimonials(data.testimonials || []);
      if (t === "usage_stats") setUsageStats(data);
      loaded.current[t] = true;
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [session]);

  useEffect(() => { load(tab); }, [tab, load]);

  const tabStyle = (id) => ({
    padding: "7px 18px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
    background: tab === id ? C.indigo : "#f0eff6", color: tab === id ? "#fff" : "#555", transition: "all 0.15s",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, height: "100%", overflow: "hidden", background: "#F8F7FA" }}>
      {/* Header */}
      <div style={{ background: C.indigo, padding: isMobile ? "13px 14px" : "17px 22px", borderBottom: `3px solid ${C.tealLight}`, flexShrink: 0, display: "flex", alignItems: "center", gap: 10 }}>
        {isMobile && (
          <button onClick={onMenuOpen} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
            <Menu size={18} color="#fff" />
          </button>
        )}
        <BarChart2 size={16} color={C.tealLight} />
        <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Admin Dashboard</span>
      </div>

      {/* Tabs */}
      <div style={{ background: "#fff", borderBottom: "1px solid #ebebf2", padding: "8px 16px", display: "flex", gap: 8, flexShrink: 0 }}>
        <button style={tabStyle("users")} onClick={() => setTab("users")}>Users</button>
        <button style={tabStyle("testimonials")} onClick={() => setTab("testimonials")}>Testimonials</button>
        <button style={tabStyle("usage_stats")} onClick={() => setTab("usage_stats")}>Usage & Cost</button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? "12px 10px" : "20px 24px" }}>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0", color: "#999", fontSize: 13 }}>Loading…</div>
        )}
        {error && (
          <div style={{ background: "#fff0f0", border: "1px solid #ffcccc", borderRadius: 8, padding: "12px 16px", fontSize: 13, color: "#c00", marginBottom: 16 }}>
            {error === "Failed to load users" || error === "Failed to load testimonials"
              ? `${error}. Make sure SUPABASE_SERVICE_ROLE_KEY is set in Vercel environment variables.`
              : error}
          </div>
        )}

        {/* ── Users ── */}
        {!loading && tab === "users" && (
          <div>
            {!creditsOn && (
              <div style={{ background: "#fff8e6", border: "1px solid #f0d9a0", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#8a6100", marginBottom: 14 }}>
                Credits aren't switched on yet. Run <code>supabase/0036_credits.sql</code> in the Brill Supabase project. Until then nobody is charged or stopped.
              </div>
            )}
            {unmatched.length > 0 && (
              <div style={{ background: "#fff8e6", border: "1px solid #f0d9a0", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#8a6100", marginBottom: 14 }}>
                <p style={{ margin: "0 0 8px", fontWeight: 700 }}>Payments that bought no credits ({unmatched.length}) — give them by hand below, then mark them sorted</p>
                {unmatched.map(p => {
                  /* The note is "<why> · GHL sent: <field names>". The why is
                     hers; the field names are for working out GHL's shape. */
                  const [why, sent] = String(p.note || "").split(" · GHL sent: ");
                  return (
                    <div key={p.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 0", borderTop: "1px solid #f0d9a0" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div><b>{p.email || "no email"}</b> · {p.amount_cents != null ? `$${(p.amount_cents / 100).toFixed(2)}` : "no amount"} · {timeAgo(p.created_at)}</div>
                        <div style={{ color: "#a07a1c" }}>{why}</div>
                        {sent && (
                          <details style={{ marginTop: 2 }}>
                            <summary style={{ cursor: "pointer", fontSize: 11, color: "#b08a3a" }}>details</summary>
                            <div style={{ fontSize: 10.5, color: "#999", wordBreak: "break-word", marginTop: 3 }}>GHL sent: {sent}</div>
                          </details>
                        )}
                      </div>
                      <button onClick={() => resolveUnmatched(p.id)}
                        style={{ fontSize: 11, padding: "3px 9px", borderRadius: 6, border: `1px solid ${C.teal}`, background: "#fff", color: C.teal, cursor: "pointer", fontWeight: 600, flexShrink: 0 }}>
                        Sorted
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {/* A table you can search, filter and sort; a row opens to the
                detail and the actions (AdminUsersTable, her ask 2026-10-08). */}
            <AdminUsersTable
              users={users} creditsOn={creditsOn} isMobile={isMobile} grantingId={grantingId}
              onGiveCredits={giveCredits} onGrantAccess={grantAccess} onOpenUsage={openUserUsage}
            />
          </div>
        )}

        {/* ── Usage & Cost ── */}
        {!loading && tab === "usage_stats" && (
          <div>
            {/* By Date / By User toggle */}
            <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
              {[{ v: "date", l: "By Date" }, { v: "user", l: "By User" }, { v: "module", l: "By Module" }].map(({ v, l }) => (
                <button key={v} onClick={() => setUsageView(v)} style={{ padding: "6px 16px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, background: usageView === v ? C.indigo : "#f0eff6", color: usageView === v ? "#fff" : "#555" }}>{l}</button>
              ))}
            </div>
            {usageStats ? (
              usageView === "date"
                ? <UsageChart dailyStats={usageStats.dailyStats} allTimeCost={usageStats.allTimeCost} allTimeTokens={usageStats.allTimeTokens} truncated={usageStats.truncated || 0} />
                : usageView === "module"
                  ? <UsageByModule modules={usageStats.moduleStats || []} tiers={usageStats.tierStats || []} />
                  : <UsageByUser users={usageStats.userStats || []} focusId={focusUserId} />
            ) : (
              <p style={{ fontSize: 13, color: "#bbb", textAlign: "center", padding: "40px 0" }}>No usage data yet.</p>
            )}
          </div>
        )}

        {/* ── Testimonials ── */}
        {!loading && tab === "testimonials" && (
          <div>
            <p style={{ margin: "0 0 16px", fontSize: 12, color: "#999" }}>{testimonials.length} testimonial{testimonials.length !== 1 ? "s" : ""}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {testimonials.map(t => (
                <div key={t.id} style={{ background: "#fff", border: "1px solid #e8e8f0", borderRadius: 12, padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: C.indigo }}>{t.userName}</span>
                      <span style={{ fontSize: 10, background: t.type === "chat_review" ? `${C.teal}14` : "#f0eff6", color: t.type === "chat_review" ? C.teal : "#888", border: t.type === "chat_review" ? `1px solid ${C.teal}33` : "1px solid #e8e8f0", borderRadius: 20, padding: "2px 8px", fontWeight: 600 }}>
                        {t.type === "chat_review" ? "AI-guided" : "Quick"}
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: "#bbb" }}>{timeAgo(t.created_at)}</span>
                  </div>
                  {t.stars > 0 && <p style={{ margin: "0 0 6px", fontSize: 14 }}>{"⭐".repeat(t.stars)}</p>}
                  {t.quote && <p style={{ margin: "0 0 8px", fontSize: 13, color: "#333", lineHeight: 1.6, fontStyle: "italic" }}>"{t.quote}"</p>}
                  {t.attribution && <p style={{ margin: "0 0 3px", fontSize: 11, color: "#888" }}>Attribution: {t.attribution}</p>}
                  {t.video && <p style={{ margin: "0 0 3px", fontSize: 11, color: "#888" }}>Video: {t.video}</p>}
                  <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
                    <button
                      onClick={() => navigator.clipboard?.writeText(t.quote)}
                      style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: `1px solid #e8e8f0`, borderRadius: 6, padding: "4px 10px", fontSize: 11, color: "#888", cursor: "pointer" }}
                    >
                      <Copy size={10} /> Copy quote
                    </button>
                  </div>
                </div>
              ))}
              {testimonials.length === 0 && !loading && (
                <p style={{ fontSize: 13, color: "#bbb", textAlign: "center", padding: "40px 0" }}>No testimonials yet.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
